import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth, api } from "../App";
import {
  RefreshCw,
  Sparkles,
  Star,
} from "lucide-react";
import AppShell from "../components/AppShell";
import UpgradePrompt from "../components/UpgradePrompt";
import { cleanPostContent } from "../utils/postContent";
import PostCard, { PostSkeleton } from "../components/PostCard";

const StarredPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [starredIds, setStarredIds] = useState(new Set());
  const [upgradePromptCode, setUpgradePromptCode] = useState(null);

  const fetchStarred = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setIsLoading(true);
      else setIsLoadingMore(true);

      const response = await api.get(`/posts/starred?page=${pageNum}&limit=10`);
      const newPosts = response.data.posts;

      if (append) {
        setPosts((prev) => [...prev, ...newPosts]);
        setStarredIds((prev) => new Set([...prev, ...newPosts.map(p => p.id)]));
      } else {
        setPosts(newPosts);
        setStarredIds(new Set(newPosts.map(p => p.id)));
      }

      setHasMore(response.data.has_more);
      setTotal(response.data.total);
      setPage(pageNum);
    } catch (error) {
      console.error("Failed to fetch starred posts:", error);
      toast.error("Failed to load starred posts");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchStarred();
  }, [fetchStarred]);

  const handleCopy = async (post) => {
    try {
      await navigator.clipboard.writeText(cleanPostContent(post.variant_content));
      setCopiedId(post.id);
      await api.post(`/posts/${post.id}/use`);
      toast.success("Copied! Ready to paste on LinkedIn.");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy post");
    }
  };

  const handleMarkUsed = async (post) => {
    try {
      await api.post(`/posts/${post.id}/use`);
      toast.success("Marked as used");
    } catch (error) {
      console.error("Failed to mark used:", error);
      toast.error("Failed to mark post as used");
    }
  };

  const handleSkip = async (post) => {
    try {
      await api.post(`/posts/${post.id}/skip`);
      setPosts((prev) => {
        const filtered = prev.filter((p) => p.id !== post.id);
        return [...filtered, post];
      });
    } catch (error) {
      console.error("Failed to skip:", error);
    }
  };

  const handleEditPost = (postId, newContent) => {
    setPosts((prev) =>
      prev.map((p) => p.id === postId ? { ...p, variant_content: newContent } : p)
    );
  };

  const handleStar = async (post) => {
    // All posts in this view are starred; clicking star = unstar + remove from list
    try {
      await api.post(`/posts/${post.id}/unstar`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setStarredIds((prev) => { const next = new Set(prev); next.delete(post.id); return next; });
      setTotal((prev) => prev - 1);
      toast.info("Removed from saved");
    } catch (error) {
      console.error("Failed to unstar:", error);
      toast.error("Failed to remove from saved");
    }
  };

  const handleLoadMore = () => {
    fetchStarred(page + 1, true);
  };

  return (
    <AppShell activeNav="starred">
      {upgradePromptCode && (
        <UpgradePrompt
          code={upgradePromptCode}
          onClose={() => setUpgradePromptCode(null)}
        />
      )}

      <div className="py-6 px-4 sm:px-6 max-w-5xl mx-auto">
        <h1 className="text-xl font-bold text-foreground mb-4">Saved Posts</h1>

        <div className="mb-6 p-4 bg-muted/50 rounded-xl">
          <p className="text-sm text-muted-foreground">
            You have <span className="font-semibold text-foreground">{total}</span> starred{" "}
            {total === 1 ? "post" : "posts"} saved for later.
          </p>
        </div>

        {isLoading ? (
          <>
            <div className="hidden xl:flex gap-4 items-start">
              <div className="flex-1 flex flex-col gap-4"><PostSkeleton /><PostSkeleton /></div>
              <div className="flex-1 flex flex-col gap-4"><PostSkeleton /></div>
            </div>
            <div className="flex xl:hidden flex-col gap-4">
              {[...Array(3)].map((_, i) => <PostSkeleton key={i} />)}
            </div>
          </>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No starred posts yet</h3>
            <p className="text-muted-foreground mb-4">
              Star posts in your feed to save them here for later.
            </p>
            <Button
              onClick={() => navigate("/feed")}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Browse Feed
            </Button>
          </div>
        ) : (
          <>
            <div className="hidden xl:flex gap-4 items-start">
              {[posts.filter((_, i) => i % 2 === 0), posts.filter((_, i) => i % 2 === 1)].map(
                (col, ci) => (
                  <div key={ci} className="flex-1 min-w-0 flex flex-col gap-4">
                    {col.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                        user={user}
                        onCopy={handleCopy}
                        onMarkUsed={handleMarkUsed}
                        onSkip={handleSkip}
                        onEdit={handleEditPost}
                        onStar={handleStar}
                        onUpgradeRequired={setUpgradePromptCode}
                        copiedId={copiedId}
                        starredIds={starredIds}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
            <div className="flex xl:hidden flex-col gap-4">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  user={user}
                  onCopy={handleCopy}
                  onMarkUsed={handleMarkUsed}
                  onSkip={handleSkip}
                  onEdit={handleEditPost}
                  onStar={handleStar}
                  onUpgradeRequired={setUpgradePromptCode}
                  copiedId={copiedId}
                  starredIds={starredIds}
                />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  data-testid="load-more-btn"
                  className="px-8"
                >
                  {isLoadingMore ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load More"
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
};

export default StarredPage;
